import React, { useEffect, useRef, useState } from "react";

import useDebounce from "hooks/useDebounce";
import usePrimeLocale from "hooks/usePrimeLocale";
import { Button } from "primereact/button";
import { Calendar, CalendarProps } from "primereact/calendar";
import { FormEvent } from "primereact/ts-helpers";
import { T } from "services/lang/LanguageService";
import { DEFAULT_DATE_FORMAT } from "utils/constants";
import { JodaTimeUtils } from "utils/joda-time-utils";
import { ChronoUnitLegacy } from "utils/time-utils";

export type CalendarWrapperProps = {
  value: Date | undefined;
  onChange(date: Date): void;
  dateFrequency?: ChronoUnitLegacy;
  debounceTimeInMs?: number;
} & Omit<CalendarProps, "value" | "onChange">;

const CalendarWrapper = ({
  value,
  onChange,
  dateFrequency,
  debounceTimeInMs,
  ...props
}: CalendarWrapperProps) => {
  usePrimeLocale();
  const valueRef = useRef<Date | null>(null);
  const [date, setDate] = useState<Date | null>(null);
  const debouncedValue = useDebounce(date, { delay: debounceTimeInMs });

  useEffect(() => {
    const handleEnterPressed = (event: KeyboardEvent) =>
      (event.code === "Enter" || event.code === "NumpadEnter") && replaceDateValue();

    window.addEventListener("keydown", handleEnterPressed);

    return () => window.removeEventListener("keydown", handleEnterPressed);
  }, []);

  useEffect(() => {
    debouncedValue && onChange(debouncedValue);
  }, [debouncedValue]);

  useEffect(() => {
    valueRef.current && (valueRef.current = null);
  }, [value]);

  const replaceDateValue = () => {
    if (valueRef.current != null && value !== valueRef.current) {
      onChange(valueRef.current);
    }
  };

  const onCalendarChange = (e: FormEvent<Date, React.SyntheticEvent<Element, Event>>) => {
    if (!e.value) return;
  
    const tempFrequency = JodaTimeUtils.convertLegacyChronoUnitToJoda(
      dateFrequency || ChronoUnitLegacy.MINUTES
    );
    const pickedDate = JodaTimeUtils.truncateTo(e.value as Date, tempFrequency);
  
    if (e.originalEvent?.type === "input") {
      valueRef.current = pickedDate;
    } else {
      setDate(pickedDate);
      onChange(pickedDate);
    }
  };
  
  const calendarFooterTemplate = () => {
    return (
      <div className="flex flex-row justify-content-between">
        <Button
          type="button"
          label={T("action.truncate-hours")}
          className="p-button-sm p-button-text p-button-outlined"
          onClick={() => {
            if (!value) return;
            const tempFrequency = JodaTimeUtils.convertLegacyChronoUnitToJoda(
              ChronoUnitLegacy.DAYS
            );
            const truncatedDate = JodaTimeUtils.truncateTo(value as Date, tempFrequency);
            onChange(truncatedDate);
          }}
          />
        <Button
          type="button"
          label={T("action.truncate-minutes")}
          className="p-button-sm p-button-text p-button-outlined"
          onClick={() => {
            if (!value) return;
            const tempFrequency = JodaTimeUtils.convertLegacyChronoUnitToJoda(
              ChronoUnitLegacy.HOURS
            );
            const truncatedDate = JodaTimeUtils.truncateTo(value as Date, tempFrequency);
            onChange(truncatedDate);
          }}
        />
      </div>
    );
  };

  return (
    <Calendar
      value={value}
      onChange={onCalendarChange}
      footerTemplate={props.showTime === false ? undefined : calendarFooterTemplate}
      showIcon
      iconPos="left"
      dateFormat={DEFAULT_DATE_FORMAT}
      showTime={props.showTime ?? true}
      onBlur={replaceDateValue}
      {...props}
      className={`p-inputtext-sm ${props.className || ""}`}
    />
  );
};

export default CalendarWrapper;
